import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    this.#queueInitialAdjacentMedia();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Promotes the selected slide and its next neighbour after navigation so
   * product-media swipes stay instant without competing with the LCP image.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;

    this.#promoteSlideImages(event.detail.index);
    this.#promoteSlideImages(event.detail.index + 1);
  };

  /**
   * Waits until the LCP candidate has finished before loading the second slide
   * during idle time. This keeps the first swipe smooth without delaying LCP.
   */
  #queueInitialAdjacentMedia() {
    const initialSlide = this.slideshow?.slides?.[this.slideshow.current];
    const initialImage = initialSlide?.querySelector('.product-media__image');

    const queue = () => {
      const loadAdjacent = () => {
        if (!this.isConnected) return;
        this.#promoteSlideImages((this.slideshow?.current ?? 0) + 1);
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadAdjacent, { timeout: 1800 });
      } else {
        window.setTimeout(loadAdjacent, 900);
      }
    };

    if (!(initialImage instanceof HTMLImageElement) || initialImage.complete) {
      queue();
      return;
    }

    initialImage.addEventListener('load', queue, { once: true, signal: this.#controller.signal });
    initialImage.addEventListener('error', queue, { once: true, signal: this.#controller.signal });
  }

  /**
   * Starts loading all preview images in a slide without changing its state.
   * @param {number} index
   */
  #promoteSlideImages(index) {
    const slide = this.slideshow?.slides?.[index];
    if (!slide) return;

    for (const image of slide.querySelectorAll('img[loading="lazy"]')) {
      image.loading = 'eager';
      image.fetchPriority = 'auto';
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
